import React, { useCallback, useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Check, Paintbrush, Download, RefreshCw, X, Loader2, Trash2 } from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import { RegionEditor } from './RegionEditor';
import { generateScreenImages } from '../../services/slot-screen-generator/SlotScreenGeneratorService';
import type { SlotScreenId } from '../../../core/types/slot-screen-generator.types';

export function ScreenFullView() {
  const fullViewScreenId = useSlotScreenGeneratorStore(s => s.fullViewScreenId);
  const fullViewIndex = useSlotScreenGeneratorStore(s => s.fullViewIndex);
  const closeFullView = useSlotScreenGeneratorStore(s => s.closeFullView);
  const navigateFullView = useSlotScreenGeneratorStore(s => s.navigateFullView);
  const selectResult = useSlotScreenGeneratorStore(s => s.selectScreenResult);
  const deleteResult = useSlotScreenGeneratorStore(s => s.deleteScreenResult);
  const syncVideoSlots = useSlotScreenGeneratorStore(s => s.syncVideoSlotsFromScreens);
  const screens = useSlotScreenGeneratorStore(s => s.screens);

  const [showRegionEditor, setShowRegionEditor] = useState(false);
  const [isRerolling, setIsRerolling] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const goPrev = useCallback(() => navigateFullView('prev'), [navigateFullView]);
  const goNext = useCallback(() => navigateFullView('next'), [navigateFullView]);

  // Keyboard handler for arrows + escape
  useEffect(() => {
    if (!fullViewScreenId || fullViewIndex === null) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFullView();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [fullViewScreenId, fullViewIndex, closeFullView, goPrev, goNext]);

  if (!fullViewScreenId || fullViewIndex === null) return null;

  const screen = screens[fullViewScreenId];
  if (!screen) return null;

  const readyResults = screen.generatedResults.filter(r => r.status === 'ready');
  const result = readyResults[fullViewIndex];
  if (!result) return null;

  const handleReroll = async () => {
    if (isRerolling) return;
    setIsRerolling(true);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const store = useSlotScreenGeneratorStore.getState();
    try {
      for await (const { result: newResult } of generateScreenImages(
        store.systemPromptJson,
        screen,
        { model: screen.generationModel ?? store.generationSettings.model, imageCount: 1 },
        controller.signal,
      )) {
        store.replaceScreenResult(fullViewScreenId as SlotScreenId, result.id, newResult);
        store.addToast({ id: Date.now().toString(), type: 'success', message: 'Image rerolled', timestamp: Date.now() });
      }
    } catch (err) {
      if (!(err instanceof Error && err.name === 'AbortError')) {
        const msg = err instanceof Error ? err.message : String(err);
        store.addToast({ id: Date.now().toString(), type: 'error', message: `Reroll failed: ${msg}`, timestamp: Date.now() });
      }
    } finally {
      setIsRerolling(false);
    }
  };

  const handleDelete = () => {
    closeFullView();
    // Defer delete to next tick so the full view closes first
    setTimeout(() => {
      deleteResult(fullViewScreenId as SlotScreenId, result.id);
      syncVideoSlots();
    }, 0);
  };

  const total = readyResults.length;
  const isSelected = result.id === screen.selectedResultId;

  return (
    <div className="absolute inset-0 z-50 bg-zinc-900 flex flex-col">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 flex-shrink-0">
        <div className="flex items-center gap-3">
          {/* Navigation */}
          {total > 1 && (
            <>
              <button
                onClick={goPrev}
                className="p-1 text-zinc-400 hover:text-white rounded hover:bg-zinc-700 transition-colors"
                title="Previous (Left Arrow)"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-xs text-zinc-400 tabular-nums">
                {fullViewIndex + 1} / {total}
              </span>
              <button
                onClick={goNext}
                className="p-1 text-zinc-400 hover:text-white rounded hover:bg-zinc-700 transition-colors"
                title="Next (Right Arrow)"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => selectResult(fullViewScreenId, result.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded transition-colors ${
              isSelected
                ? 'bg-blue-600 text-white'
                : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
            }`}
            title="Choose for Video"
          >
            <Check size={12} />
            {isSelected ? 'Selected' : 'Choose for Video'}
          </button>
          <button
            onClick={() => setShowRegionEditor(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-700 text-zinc-300 hover:bg-zinc-600 rounded transition-colors"
            title="Edit Region"
          >
            <Paintbrush size={12} />
            Edit Region
          </button>
          <button
            onClick={() => {
              if (result.imageUrl) {
                const a = document.createElement('a');
                a.href = result.imageUrl;
                a.download = `screen-${fullViewScreenId}-${fullViewIndex + 1}.png`;
                a.click();
              }
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-700 text-zinc-300 hover:bg-zinc-600 rounded transition-colors"
            title="Save Image"
          >
            <Download size={12} />
            Save
          </button>
          <button
            onClick={handleReroll}
            disabled={isRerolling}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-700 text-zinc-300 hover:bg-zinc-600 disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed rounded transition-colors"
            title="Reroll"
          >
            {isRerolling ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {isRerolling ? 'Rerolling...' : 'Reroll'}
          </button>
          <button
            onClick={handleDelete}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-zinc-700 text-zinc-300 hover:bg-red-900/80 rounded transition-colors"
            title="Remove image"
          >
            <Trash2 size={12} />
            Remove
          </button>
          <button
            onClick={closeFullView}
            className="p-1.5 text-zinc-400 hover:text-white rounded hover:bg-zinc-700 transition-colors"
            title="Close (Escape)"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div className="flex-1 flex items-center justify-center overflow-hidden p-4">
        {result.imageUrl && (
          <img
            src={result.imageUrl}
            alt={`Result ${fullViewIndex + 1}`}
            className="max-w-full max-h-full object-contain rounded-lg"
          />
        )}
      </div>

      {/* Info bar */}
      <div className="flex-shrink-0 border-t border-zinc-800 px-4 py-2">
        <div className="flex items-start gap-6">
          {/* Metadata */}
          <div className="flex items-center gap-3 text-xs text-zinc-500 flex-shrink-0">
            <span className="text-zinc-400 font-medium">{result.model}</span>
            <span>{result.width}&times;{result.height}</span>
            <span>{new Date(result.timestamp).toLocaleString()}</span>
          </div>

          {/* Prompt text */}
          <div className="flex-1 min-w-0 max-h-16 overflow-y-auto">
            <p className="text-xs text-zinc-400 leading-relaxed">{result.prompt}</p>
          </div>
        </div>
      </div>

      {/* Region Editor overlay */}
      {showRegionEditor && (
        <RegionEditor
          screenId={fullViewScreenId}
          resultId={result.id}
          imageUrl={result.imageUrl}
          imageWidth={result.width}
          imageHeight={result.height}
          onClose={() => setShowRegionEditor(false)}
        />
      )}
    </div>
  );
}
